import React, { Suspense } from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import LayoutWrapper from '../components/layout/LayoutWrapper';
import ErrorBoundary from '../components/shared/ErrorBoundary';
import ChunkErrorBoundary from '../components/routing/ChunkErrorBoundary';
import lazyWithRetry from '../utils/lazyWithRetry';
import ScrollManager from '../components/routing/ScrollManager';
import DocumentTitle from '../components/routing/DocumentTitle';
import { SHOW_DEV_TOOLS } from '../config/devTools';
import SEOHead from '../components/seo/SEOHead';

// Contrôle d'accès
import EnhancedPrivateRoute from '../auth/EnhancedPrivateRoute';
import AdminRoute from '../auth/AdminRoute';
import PublicRoute from '../auth/PublicRoute';
import HomeRoute from '../auth/HomeRoute';
import EmailConfirmation from '../components/auth/EmailConfirmation';

// L'accueil est chargé immédiatement ; les autres pages à la demande (fichier principal plus léger),
// avec nouveaux essais automatiques si le téléchargement échoue (voir utils/lazyWithRetry.js)
import Home from '../pages/Home';

// Espace personnel et administration
const Dashboard = lazyWithRetry(() => import('../pages/Dashboard'));
const AdminDashboard = lazyWithRetry(() => import('../pages/AdminDashboard'));
const Notifications = lazyWithRetry(() => import('../pages/Notifications'));
const Settings = lazyWithRetry(() => import('../pages/Settings'));
const PaymentReturn = lazyWithRetry(() => import('../pages/PaymentReturn'));
const Profil = lazyWithRetry(() => import('../pages/Profil'));
const ModifierProfil = lazyWithRetry(() => import('../pages/ModifierProfil'));
const MesProjets = lazyWithRetry(() => import('../pages/MesProjets'));
const SubmitProject = lazyWithRetry(() => import('../pages/SubmitProject'));

// Authentification
const Register = lazyWithRetry(() => import('../pages/Register'));
const ForgotPassword = lazyWithRetry(() => import('../pages/ForgotPassword'));
const ResetPassword = lazyWithRetry(() => import('../pages/ResetPassword'));

// Investir
const Projects = lazyWithRetry(() => import('../pages/Projects'));
const ProjectDetailPage = lazyWithRetry(() => import('../pages/ProjectDetailPage'));
const InvestissementRevenus = lazyWithRetry(() => import('../pages/InvestissementRevenus'));
const EpargnePositive = lazyWithRetry(() => import('../pages/EpargnePositive'));
const Diaspora = lazyWithRetry(() => import('../pages/Diaspora'));
const EligibilitePage = lazyWithRetry(() => import('../pages/EligibilitePage'));
const IncubateurPage = lazyWithRetry(() => import('../pages/IncubateurPage'));
const PourQuiPage = lazyWithRetry(() => import('../pages/PourQuiPage'));
const AgendaPage = lazyWithRetry(() => import('../pages/AgendaPage'));
const EtudesDeCasPage = lazyWithRetry(() => import('../pages/EtudesDeCasPage'));
const RessourcesPage = lazyWithRetry(() => import('../pages/RessourcesPage'));
const RessourcePage = lazyWithRetry(() => import('../pages/RessourcePage'));
const PlanAffairesPage = lazyWithRetry(() => import('../pages/PlanAffairesPage'));
const BudgetPrevisionnelPage = lazyWithRetry(() => import('../pages/BudgetPrevisionnelPage'));
const DevenirMentorPage = lazyWithRetry(() => import('../pages/DevenirMentorPage'));
const DonPage = lazyWithRetry(() => import('../pages/DonPage'));
const LoginPage = lazyWithRetry(() => import('../pages/LoginPage'));
const IdentityVerificationPage = lazyWithRetry(() => import('../pages/IdentityVerificationPage'));
const SupportInvestisseurs = lazyWithRetry(() => import('../pages/SupportInvestisseurs'));
const OnboardingPage = lazyWithRetry(() => import('../pages/OnboardingPage'));

// Entreprendre
const FinancementPage = lazyWithRetry(() => import('../pages/FinancementPage'));
const SupportEntrepreneurs = lazyWithRetry(() => import('../pages/SupportEntrepreneurs'));
const CriteresSelection = lazyWithRetry(() => import('../pages/CriteresSelection'));

// À propos
const AboutPage = lazyWithRetry(() => import('../pages/AboutPage'));
const Vision = lazyWithRetry(() => import('../pages/Vision'));
const Equipe = lazyWithRetry(() => import('../pages/Equipe'));
const Partenaires = lazyWithRetry(() => import('../pages/Partenaires'));
const Partnership = lazyWithRetry(() => import('../pages/Partnership'));
const EspacePresse = lazyWithRetry(() => import('../pages/EspacePresse'));
const RecrutementPage = lazyWithRetry(() => import('../pages/RecrutementPage'));
const ContactPage = lazyWithRetry(() => import('../pages/ContactPage'));
const VerifierRecuPage = lazyWithRetry(() => import('../pages/VerifierRecuPage'));
const BlogPage = lazyWithRetry(() => import('../pages/BlogPage'));
const BlogPostPage = lazyWithRetry(() => import('../pages/BlogPostPage'));
const NewsletterPage = lazyWithRetry(() => import('../pages/NewsletterPage'));
const SearchResults = lazyWithRetry(() => import('../pages/SearchResults'));

// Engagements et chiffres
const Engagements = lazyWithRetry(() => import('../pages/Engagements'));
const Transparence = lazyWithRetry(() => import('../pages/Transparence'));
const ImpactSocial = lazyWithRetry(() => import('../pages/ImpactSocial'));
const Ecologie = lazyWithRetry(() => import('../pages/Ecologie'));
const Inclusion = lazyWithRetry(() => import('../pages/Inclusion'));
const Securite = lazyWithRetry(() => import('../pages/Securite'));
const StatsPage = lazyWithRetry(() => import('../pages/StatsPage'));
const RapportsPage = lazyWithRetry(() => import('../pages/RapportsPage'));
const TableauxDeBordPage = lazyWithRetry(() => import('../pages/TableauxDeBordPage'));
const IndicateursPerformance = lazyWithRetry(() => import('../pages/IndicateursPerformance'));
const FeaturesPage = lazyWithRetry(() => import('../pages/FeaturesPage'));

// Informations légales
const CGUPage = lazyWithRetry(() => import('../pages/CGUPage'));
const MentionsLegales = lazyWithRetry(() => import('../pages/MentionsLegales'));
const PolitiqueConfidentialite = lazyWithRetry(() => import('../pages/PolitiqueConfidentialiteSimple'));
const CookiesPremium = lazyWithRetry(() => import('../pages/CookiesPremium'));
const Reclamations = lazyWithRetry(() => import('../pages/Reclamations'));

// Outils techniques (développement uniquement, voir src/config/devTools.js)
const DiagnosticPage = lazyWithRetry(() => import('../pages/DiagnosticPage'));
const DiagnosticComplet = lazyWithRetry(() => import('../pages/DiagnosticComplet'));
const TestSupabaseAuth = lazyWithRetry(() => import('../components/TestSupabaseAuth'));
const BackendIntegrationTest = lazyWithRetry(() => import('../pages/BackendIntegrationTest'));
const NavigationTester = lazyWithRetry(() => import('../pages/NavigationTester'));
const VisualFeedbackDemo = lazyWithRetry(() => import('../pages/VisualFeedbackDemo'));
const LogoShowcase = lazyWithRetry(() => import('../pages/LogoShowcase'));

/**
 * 🛣️ ROUTES DU SITE
 * =================
 * - Pages publiques, espace personnel (EnhancedPrivateRoute), administration (AdminRoute)
 * - Anciennes adresses redirigées vers une page de référence unique (pas de doublons)
 * - Outils techniques uniquement en développement (src/config/devTools.js)
 * - Pages chargées à la demande, avec un écran de chargement sobre
 */

// Écran affiché pendant le chargement d'une page
const LoadingFallback = () => (
  <div className="flex min-h-[60vh] items-center justify-center bg-white" role="status" aria-live="polite">
    <div className="flex items-center gap-3 text-gray-600">
      <span className="h-6 w-6 animate-spin rounded-full border-2 border-blue-200 border-t-blue-700" aria-hidden="true" />
      <span className="font-medium">Chargement de la page…</span>
    </div>
  </div>
);

// Page introuvable (exclue des moteurs de recherche : noindex)
const NotFoundPage = () => (
  <div className="flex min-h-[60vh] items-center justify-center bg-gray-50 px-4 py-16">
    <SEOHead title="Page introuvable" description="Cette page n'existe pas ou a été déplacée." noindex />
    <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-sm">
      <p className="text-sm font-semibold uppercase tracking-wider text-orange-600">Erreur 404</p>
      <h1 className="mt-2 text-3xl font-bold text-gray-900">Page introuvable</h1>
      <p className="mt-3 text-gray-600">La page que vous cherchez n'existe pas ou a été déplacée.</p>
      <div className="mt-8 flex flex-col gap-3">
        <Link to="/" className="inline-flex min-h-[48px] items-center justify-center rounded-xl bg-orange-500 px-6 py-3 font-semibold text-white hover:bg-orange-600">
          Retour à l'accueil
        </Link>
        <Link to="/projects" className="inline-flex min-h-[48px] items-center justify-center rounded-xl border-2 border-blue-700 px-6 py-3 font-semibold text-blue-800 hover:bg-blue-50">
          Voir les projets
        </Link>
      </div>
    </div>
  </div>
);

/**
 * Composant principal de routage
 */
const EnhancedAppRoutes = () => {
  const location = useLocation();

  return (
    <LayoutWrapper>
      {/* Défilement cohérent à chaque navigation (haut de page, ancres, retour/suivant) */}
      <ScrollManager />
      <DocumentTitle />
      {/* Une erreur dans une page reste confinée à cette page (session et navigation conservées) ;
          la clé réinitialise l'affichage d'erreur à chaque changement de page. */}
      <ErrorBoundary key={location.pathname}>
      {/* Page impossible à télécharger (connexion coupue, site mis à jour) : écran « Recharger la page ».
          Placé ici, au plus près des pages, sinon ErrorBoundary intercepte l'erreur avant lui. */}
      <ChunkErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
        {/* 🌐 Accueil et authentification */}
        <Route path="/" element={<HomeRoute><Home /></HomeRoute>} />
        <Route path="/app" element={<Navigate to="/dashboard" replace />} />
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        {/* Après l'inscription : vérification d'identité (étape suivante du parcours) */}
        <Route path="/register" element={<PublicRoute redirectTo="/verifier-identite" redirectState={{ welcome: true }}><Register /></PublicRoute>} />
        <Route path="/signup" element={<Navigate to="/register" replace />} />
        <Route path="/confirm-email" element={<PublicRoute><EmailConfirmation /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
        {/* Hors PublicRoute : le lien de réinitialisation ouvre une session, la page doit rester affichée */}
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* 🔒 Espace personnel */}
        <Route path="/dashboard" element={<EnhancedPrivateRoute><Dashboard /></EnhancedPrivateRoute>} />
        <Route path="/verifier-identite" element={<EnhancedPrivateRoute><IdentityVerificationPage /></EnhancedPrivateRoute>} />
        <Route path="/profil" element={<EnhancedPrivateRoute><Profil /></EnhancedPrivateRoute>} />
        <Route path="/modifier-profil" element={<EnhancedPrivateRoute><ModifierProfil /></EnhancedPrivateRoute>} />
        <Route path="/notifications" element={<EnhancedPrivateRoute><Notifications /></EnhancedPrivateRoute>} />
        <Route path="/settings" element={<EnhancedPrivateRoute><Settings /></EnhancedPrivateRoute>} />
        <Route path="/mes-projets" element={<EnhancedPrivateRoute><MesProjets /></EnhancedPrivateRoute>} />
        <Route path="/submit" element={<EnhancedPrivateRoute><SubmitProject /></EnhancedPrivateRoute>} />
        <Route path="/paiement/retour" element={<EnhancedPrivateRoute><PaymentReturn /></EnhancedPrivateRoute>} />
        <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />

        {/* 💼 Investir */}
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:id" element={<ProjectDetailPage />} />
        <Route path="/project/:id" element={<ProjectDetailPage />} />
        <Route path="/investissement-revenus" element={<InvestissementRevenus />} />
        <Route path="/epargne-positive" element={<EpargnePositive />} />
        <Route path="/diaspora" element={<Diaspora />} />
        <Route path="/etudes-de-cas" element={<EtudesDeCasPage />} />
        <Route path="/support-investisseurs" element={<SupportInvestisseurs />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/alertes" element={<Navigate to="/settings#notifications" replace />} />

        {/* 🚀 Entreprendre */}
        <Route path="/financement" element={<FinancementPage />} />
        <Route path="/support-entrepreneurs" element={<SupportEntrepreneurs />} />
        <Route path="/criteres-selection" element={<CriteresSelection />} />
        <Route path="/eligibilite" element={<EligibilitePage />} />
        <Route path="/incubateur" element={<IncubateurPage />} />
        <Route path="/ressources" element={<RessourcesPage />} />
        <Route path="/ressources/plan-d-affaires" element={<PlanAffairesPage />} />
        <Route path="/ressources/budget-previsionnel" element={<BudgetPrevisionnelPage />} />
        <Route path="/ressources/:slug" element={<RessourcePage />} />
        <Route path="/devenir-mentor" element={<DevenirMentorPage />} />
        <Route path="/faire-un-don" element={<DonPage />} />

        {/* ℹ️ À propos */}
        <Route path="/about" element={<AboutPage />} />
        <Route path="/verifier-recu" element={<VerifierRecuPage />} />
        <Route path="/verifier-recu/:code" element={<VerifierRecuPage />} />
        <Route path="/pour-qui" element={<PourQuiPage />} />
        <Route path="/agenda" element={<AgendaPage />} />
        <Route path="/vision" element={<Vision />} />
        <Route path="/equipe" element={<Equipe />} />
        <Route path="/partenaires" element={<Partenaires />} />
        <Route path="/partnership" element={<Partnership />} />
        <Route path="/presse" element={<EspacePresse />} />
        <Route path="/recrutement" element={<RecrutementPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/newsletter" element={<NewsletterPage />} />
        <Route path="/search" element={<SearchResults />} />

        {/* 🌱 Engagements et chiffres */}
        <Route path="/engagements" element={<Engagements />} />
        <Route path="/transparence" element={<Transparence />} />
        <Route path="/impact-social" element={<ImpactSocial />} />
        <Route path="/ecologie" element={<Ecologie />} />
        <Route path="/inclusion" element={<Inclusion />} />
        <Route path="/securite" element={<Securite />} />
        <Route path="/stats" element={<StatsPage />} />
        <Route path="/rapports" element={<RapportsPage />} />
        <Route path="/tableaux-de-bord" element={<TableauxDeBordPage />} />
        <Route path="/indicateurs-performance" element={<IndicateursPerformance />} />
        <Route path="/features" element={<FeaturesPage />} />

        {/* 📜 Informations légales */}
        <Route path="/cgu" element={<CGUPage />} />
        <Route path="/mentions-legales" element={<MentionsLegales />} />
        <Route path="/confidentialite" element={<PolitiqueConfidentialite />} />
        <Route path="/cookies" element={<CookiesPremium />} />
        <Route path="/reclamations" element={<Reclamations />} />

        {/* ↪️ Anciennes adresses et alias : redirigés vers la page de référence */}
        <Route path="/analytics" element={<Navigate to="/profil" replace />} />
        <Route path="/profile" element={<Navigate to="/profil" replace />} />
        <Route path="/investment-history" element={<Navigate to="/profil" replace />} />
        <Route path="/submit-project" element={<Navigate to="/submit" replace />} />
        <Route path="/projets" element={<Navigate to="/projects" replace />} />
        <Route path="/projects-list" element={<Navigate to="/projects" replace />} />
        <Route path="/investissement" element={<Navigate to="/investissement-revenus" replace />} />
        <Route path="/invest" element={<Navigate to="/investissement-revenus" replace />} />
        <Route path="/epargne" element={<Navigate to="/epargne-positive" replace />} />
        <Route path="/financer-projet" element={<Navigate to="/financement" replace />} />
        <Route path="/candidature" element={<Navigate to="/recrutement#candidature" replace />} />
        <Route path="/postuler" element={<Navigate to="/recrutement#candidature" replace />} />
        <Route path="/carrieres" element={<Navigate to="/recrutement" replace />} />
        <Route path="/carriere" element={<Navigate to="/recrutement" replace />} />
        <Route path="/statistiques" element={<Navigate to="/stats" replace />} />
        <Route path="/terms" element={<Navigate to="/cgu" replace />} />
        <Route path="/privacy" element={<Navigate to="/confidentialite" replace />} />
        <Route path="/modern-auth-test" element={<Navigate to="/login" replace />} />

        {/* 🔧 Outils techniques : développement uniquement, réservés aux administrateurs */}
        {SHOW_DEV_TOOLS && (
          <>
            <Route path="/diagnostic" element={<AdminRoute><DiagnosticPage /></AdminRoute>} />
            <Route path="/diagnostic-complet" element={<AdminRoute><DiagnosticComplet /></AdminRoute>} />
            <Route path="/test-auth" element={<AdminRoute><TestSupabaseAuth /></AdminRoute>} />
            <Route path="/backend-test" element={<AdminRoute><BackendIntegrationTest /></AdminRoute>} />
            <Route path="/navigation-test" element={<AdminRoute><NavigationTester /></AdminRoute>} />
            <Route path="/feedback-demo" element={<AdminRoute><VisualFeedbackDemo /></AdminRoute>} />
            <Route path="/logo-showcase" element={<AdminRoute><LogoShowcase /></AdminRoute>} />
          </>
        )}

        {/* 🚫 Page introuvable */}
        <Route path="*" element={<NotFoundPage />} />
        </Routes>
    </Suspense>
      </ChunkErrorBoundary>
      </ErrorBoundary>
    </LayoutWrapper>
  );
};

export default EnhancedAppRoutes;